<template>
    <div id="app">
        <Disclosure v-if="loggedIn && isEmailVerified && $route.name !=='Referee Portal'" v-slot="{ open }" as="nav" class="bg-lshtm-primary">
            <div class="w-full md:w-4/5 m-auto py-6 px-0 md:px-12">
                <div class="relative flex items-center justify-between h-16">
                    <div class="absolute inset-y-0 left-0 flex items-center sm:hidden">
                        <!-- Mobile menu button-->
                        <DisclosureButton class="inline-flex items-center justify-center p-2 rounded-md text-black-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
                            <span class="sr-only">Open main menu</span>
                            <MenuIcon v-if="!open" aria-hidden="true" class="block h-6 w-6 bg-white" />
                            <XIcon v-else aria-hidden="true" class="block h-6 w-6 bg-white" />
                        </DisclosureButton>
                    </div>
                    <div class="flex-1 flex items-center justify-center sm:items-stretch sm:justify-start">
                        <div class="flex-shrink-0 flex items-center">
                            <img alt="lshtm-logo" class="w-1/2" src="https://publicapis.lshtm.ac.uk/global/logo?height=120&inverse=true" />
                        </div>
                        <div class="hidden sm:block sm:ml-6 align-baseline">
                            <div class="flex space-x-4 mt-2">
                                <a v-for="item in navigation"
                                   :key="item.name"
                                   :class="[item.name === $route.name ? 'border-b-2 border-black text-white' : 'text-white hover:text-white', 'px-3 py-2 text-sm font-bold']"

                                   :href="item.href"
                                >
                                    {{ item.label }}
                                </a>
                            </div>
                        </div>
                    </div>
                    <div class="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
                        <!-- Profile dropdown -->
                        <Menu as="div" class="ml-3 relative">
                            <div>
                                <MenuButton
                                    class="flex text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
                                >
                                    <span class="sr-only">Open user menu</span>
                                    <icon class="w-6 h-6" name="avatar" />
                                </MenuButton>
                            </div>
                            <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
                                <MenuItems class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                                    <MenuItem v-if="loggedIn" v-slot="{ active }">
                                        <router-link :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-black-700']" :to="{name: 'Home'}">Dashboard</router-link>
                                    </MenuItem>
                                    <MenuItem v-if="loggedIn" v-slot="{ active }">
                                        <router-link :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-black-700']" :to="{name: 'Profile'}">Profile</router-link>
                                    </MenuItem>
                                    <MenuItem v-if="loggedIn && $route.name!=='Referee Portal'" v-slot="{ active }">
                                        <router-link
                                            :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-black-700']"
                                            :to="{ name: 'Logout' }"
                                        >
                                            Log out
                                        </router-link>
                                    </MenuItem>
                                </MenuItems>
                            </transition>
                        </Menu>
                    </div>
                </div>
            </div>

            <DisclosurePanel class="sm:hidden">
                <div class="px-2 pt-2 pb-3 space-y-1">
                    <DisclosureButton v-for="item in navigation" :key="item.name" :aria-current="item.current ? 'page' : undefined" :class="[item.current ? 'bg-gray-900 text-white' : 'text-white hover:bg-gray-700 hover:text-white', 'block px-3 py-2 rounded-md text-base font-medium']" :href="item.href" as="a">{{ item.name }}</DisclosureButton>
                </div>
            </DisclosurePanel>
        </Disclosure>
        <div v-if="settings?.sitewide_notice" class="publicNotice bg-yellow-400 max-w-7xl mx-auto p-2 px-6 md:px-12">
            <p>{{ settings?.sitewide_notice?.value }}</p>
        </div>

        <router-view @getUser="getUserFromChild" class="bg-lshtm-secondary" style="height: calc(100vh - 176px); overflow-y: auto"/>

        <div class="bg-lshtm-secondary">
            <p><span class="text-sm">v1.2.12</span></p>
            <p class="pb-5"><a href="//lshtm.ac.uk" target="_blank"><span class="text-sm">London School of Hygiene and Tropical Medicine</span></a></p>
        </div>
    </div>
</template>

<script>
import {Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems} from '@headlessui/vue'
import {MenuIcon, XIcon} from '@heroicons/vue/outline'
import Icon from '@/shared/Icon'

export default {
    name: 'App',
    components: {
        Disclosure,
        DisclosureButton,
        DisclosurePanel,
        Menu,
        MenuButton,
        MenuItem,
        MenuItems,
        MenuIcon,
        XIcon,
        Icon,
    },
    data() {
        return {
            navigation: [
                { name: 'Home', label: 'Dashboard', href: '/'},
                { name: 'ViewApplication', label: 'Applications', href: '/applications'},
            ],
            name: null,
            verified: false,
        }
    },
    computed: {
        loggedIn() {
            return this.$store.getters.loggedIn
        },
        settings() {
            return this.$store.getters.settings
        },
        isEmailVerified() {
            return this.$store.getters.verifiedEmail
        },
    },
    watch: {
        settings(data) {
            this.settings = data
        },
    },
    mounted() {
        if (this.loggedIn) {
        this.getSettings()
        }
    },
    methods: {
        getUserFromChild(name) {
            this.name = name
        },
        getSettings() {
            this.$store
                .dispatch('getSettings')
                .then(() => {})
                .catch(() => {})
        },
    },
}
</script>

<style>
#app {
    font-family: Open Sans Bold, Helvetica, Arial, sans-serif;
    text-align: center;
}

/* Public notice */
.publicNotice {
    background-color: wheat;
    padding: 15px;
    text-align: left;
    display: none;
}
.publicNotice ol, .publicNotice ul {
    margin-left: 30px;
    list-style: auto;
}
</style>